import { useEffect } from 'react';
import {
  Outlet,
  createSearchParams,
  matchPath,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { LoadingSpinner } from '../../components';
import { PATHS } from '../../routes';

import { AuthState, useAuth } from './AuthProvider';

const guestPages = [
  PATHS.Login,
  PATHS.Register,
  PATHS.EmailVerification,
  PATHS.ForgotPassword,
  PATHS.ResetPassword,
];

const publicPages = [PATHS.Terms];

const defaultRouteWhitelist = [
  ...guestPages,
  ...publicPages,
  PATHS.Home,
  PATHS.Error,
  PATHS.ExamResult,
  PATHS.SessionExpired,
  ...Object.values(PATHS.Exam),
  ...Object.values(PATHS.MyScore),
];

function AuthValidator({ children }: { children?: JSX.Element }) {
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { authState } = useAuth();

  const requiresOnlyGuest = guestPages.some((path) => matchPath(path, pathname));
  const existPath = defaultRouteWhitelist.some((path) => matchPath(path, pathname));

  useEffect(() => {
    if (authState === AuthState.LOADING) return;

    const from = encodeURIComponent(window.location.href);

    if (!existPath && authState === AuthState.NOT_LOGGED_IN) {
      navigate({
        pathname: PATHS.Home,
        search: createSearchParams({ continue: from }).toString(),
      });
      return;
    }

    if (requiresOnlyGuest && authState === AuthState.LOGGED_IN) {
      const continueQuery = searchParams.get('continue');

      const continuePath = (continueQuery as string) ?? '/exam';
      let destination = `${decodeURIComponent(continuePath)}`;
      if (pathname.includes(destination)) destination = '/exam';
      navigate(destination);
    }
    // do effect only if authentication state changed
  }, [authState]);

  // do not render anything if authentication state is loading
  if (authState === AuthState.LOADING) {
    return (
      <div className='grid h-dvh w-screen place-items-center'>
        <LoadingSpinner />
      </div>
    );
  }

  // do not render anything if authentication state is not logged in
  if (!existPath && authState === AuthState.NOT_LOGGED_IN) {
    return null;
  }

  // do not render anything if current pathname is included in guestPages list
  if (requiresOnlyGuest && authState === AuthState.LOGGED_IN) {
    return null;
  }

  return children ?? <Outlet />;
}

export default AuthValidator;
